import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";

import moment from "moment";

import { IconButton, Stack, Tooltip } from "@mui/material"; 
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

import CustomRightDrawer from "components/atoms/CustomRightDrawer";
import CreateProduct from "pages/pharmacy/components/CreateProduct";
import Pagination from "components/molecules/pagination/Pagination";
import { currencyFormatter, numberFormatter } from "utils/numberFormatter";


const Row = ({ i, toggleViewModal, row, onEdit, onDelete, isSelected, onToggleSelection }) => {
  return (
    <TableRow
      selected={isSelected} // Highlight row if selected
      sx={{
        "&:nth-of-type(odd)": {
          backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "background.custom",
        },
        "&:last-child td, &:last-child th": { border: 0 },
      }}
    >
      {/* [NEW] Row Checkbox */}
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          checked={isSelected}
          onChange={() => onToggleSelection(row._id)}
        />
      </TableCell>

      <TableCell component="th" scope="row" align="left" sx={{ width: "50px" }}>
        {i + 1}
      </TableCell>

      <TableCell align="left">{row?.drugName}</TableCell>
      <TableCell align="left">{row?.strength}</TableCell>

      <TableCell align="left">
        {currencyFormatter(row?.salesPrice || 0)}
      </TableCell>
      <TableCell align="left">
        {currencyFormatter(row?.purchasePrice || 0)}
      </TableCell>
      <TableCell align="left">
        {numberFormatter(row?.availableQuantity)}
      </TableCell>

      <TableCell align="left">
        {row?.expiryDate ? (
          <span style={{ 
            color: moment(row.expiryDate).isBefore(moment().add(3, 'months')) ? 'red' : 'inherit',
            fontWeight: moment(row.expiryDate).isBefore(moment().add(3, 'months')) ? 'bold' : 'normal'
          }}>
            {moment(row.expiryDate).format("DD-MM-YYYY")}
          </span>
        ) : (
          "N/A"
        )}
      </TableCell>

      <TableCell align="left">
        {moment(new Date(row.createdAt)).format("MMMM DD, YYYY")}
      </TableCell>

      <TableCell align="left">
        <Stack direction="row" spacing={0}>
          <Tooltip title="View Details">
            <IconButton
              onClick={() => toggleViewModal(row)}
              color="primary"
              size="small"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Product">
            <IconButton
              onClick={() => onEdit(row)}
              color="secondary"
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Product">
            <IconButton
              onClick={() => onDelete(row._id)}
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default function AvailableStockTable({
  data,
  currentPage,
  handlePageChange,
  onEdit,   
  onDelete, 
  selectedIds = [],
  onSelectionChange,
}) {
  const modalRef = React.useRef(null);
  
  const [viewStockItem, setViewStockItem] = React.useState(null);

  // Renamed to be specific to "View" to avoid confusion with the Edit Modal in parent
  const toggleViewModal = (item) => {
    setViewStockItem(item);
    modalRef?.current?.handleToggle();
  };

  // Logic for selecting a single item
  const handleToggleSelection = (id) => {
    const currentIndex = selectedIds.indexOf(id);
    const newSelected = [...selectedIds];

    if (currentIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    onSelectionChange(newSelected);
  };

  // Logic for "Select All" on the current page
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const pageIds = data.products.map((n) => n._id);
      // Merge current page IDs with existing selection (avoiding duplicates)
      const newSelected = Array.from(new Set([...selectedIds, ...pageIds]));
      onSelectionChange(newSelected);
      return;
    }
    // Deselect only the items on the current page
    const pageIds = data.products.map((n) => n._id);
    const newSelected = selectedIds.filter((id) => !pageIds.includes(id));
    onSelectionChange(newSelected);
  };

  const isAllSelected = data.products.length > 0 && 
    data.products.every((row) => selectedIds.includes(row._id));
  
  const isSomeSelected = data.products.some((row) => selectedIds.includes(row._id)) && !isAllSelected;

  return (
    <>
      {data.products.length ? (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={isSomeSelected}
                      checked={isAllSelected}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "70px" }}>
                    S/N
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Medicine Name
                  </TableCell>

                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Strength
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Sales Price
                  </TableCell>

                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Purchase Price
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Stock Qty
                  </TableCell>

                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Expiry Date
                  </TableCell>

                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Last Updated
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.products?.map((row, i) => (
                  <Row
                    key={row?._id}
                    row={row}
                    toggleViewModal={toggleViewModal} 
                    onEdit={onEdit}     
                    onDelete={onDelete} 
                    i={i}
                    isSelected={selectedIds.indexOf(row._id) !== -1}
                    onToggleSelection={handleToggleSelection}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            currentPage={currentPage}
            totalCount={data?.count || 5}
            pageSize={10}
            activeColor="secondary.main"
            onPageChange={handlePageChange}
          />
        </>
      ) : (
        "No data found"
      )}

      <CustomRightDrawer ref={modalRef} title={"Stock Item Detail"} subTitle="">
        <CreateProduct editData={viewStockItem} />
      </CustomRightDrawer>
    </>
  );
}
